// web/src/shell/TrustIndicator.tsx — the persistent "what the server can see"
// trust indicator in the app shell's top bar (M5 / content-guide §3, §4). A
// compact shield with the verbatim collapsed label that expands into a popover:
// the "what the server can see / can never see" split, then a progressive-
// disclosure "How this works" explainer (plain language first, expanders for the
// curious). This is the product's most important honesty surface — copy is
// load-bearing and lives in lib/trust. Token-only; keyboard + screen-reader
// friendly (Escape closes; outside-click closes; aria-expanded on the trigger).
import React, { useEffect, useId, useRef, useState } from 'react';
import { ShieldCheck, ChevronDown } from 'lucide-react';
import { Icon } from '../components/ui/Icon';
import {
  TRUST_ARIA_LABEL,
  TRUST_COLLAPSED_LABEL,
  TRUST_EXPLAINER,
  TRUST_PANEL_TITLE,
  WhatTheServerCanSee,
} from '../lib/trust';

export function TrustIndicator(): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  // Close on Escape and on a click outside the indicator.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    function onClick(e: MouseEvent): void {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div className="bgTrust" ref={wrapRef}>
      <button
        type="button"
        className="bgTrustTrigger"
        aria-label={TRUST_ARIA_LABEL}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon icon={ShieldCheck} size="sm" tone="accent" />
        <span className="bgTrustLabel">{TRUST_COLLAPSED_LABEL}</span>
      </button>

      {open && (
        <div className="bgTrustPanel" id={panelId} role="dialog" aria-label={TRUST_PANEL_TITLE}>
          <h2 className="bgTrustTitle">{TRUST_PANEL_TITLE}</h2>
          <WhatTheServerCanSee />

          <button
            type="button"
            className="bgTrustHowToggle"
            aria-expanded={howOpen}
            onClick={() => setHowOpen((h) => !h)}
          >
            How this works
            <Icon icon={ChevronDown} size="xs" className={howOpen ? 'bgTrustChevOpen' : undefined} />
          </button>

          {howOpen && (
            <div className="bgTrustHow">
              {TRUST_EXPLAINER.map((s) => {
                const isOpen = expanded === s.id;
                return (
                  <div key={s.id} className="bgTrustItem">
                    <p className="bgTrustSummary">{s.summary}</p>
                    <button
                      type="button"
                      className="bgTrustExpander"
                      aria-expanded={isOpen}
                      onClick={() => setExpanded(isOpen ? null : s.id)}
                    >
                      {s.expanderLabel}
                      <Icon
                        icon={ChevronDown}
                        size="xs"
                        className={isOpen ? 'bgTrustChevOpen' : undefined}
                      />
                    </button>
                    {isOpen && <div className="bgTrustDetail">{s.detail}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TrustIndicator;
